import React, {useEffect, useState} from "react";
import { Controller, Control, FieldErrors } from "react-hook-form";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { offset } from "@floating-ui/react";

type AvailableDatePickerProps = {
    name: string;
    label?: string;
    control: Control<any>;
    errors: FieldErrors;
    required?: boolean;
    className?: string;
    wrapperClassName?: string;
    availableDates?: Date[];
    colorUnavailableDates?: boolean;
};

const CustomInput = React.forwardRef<HTMLInputElement, any>(
    ({ value, onClick, onChange, placeholder, className }, ref) => (
        <input
            type="text"
            readOnly
            ref={ref}
            onClick={onClick}
            onChange={onChange}
            value={value}
            placeholder={placeholder}
            className={className}
        />
    )
);
CustomInput.displayName = "CustomInput";

export const AvailableDatePicker: React.FC<AvailableDatePickerProps> = ({
        name,
        label = null,
        control,
        errors,
        required = false, 
        wrapperClassName = "mb-3",
        className = "form-control form-control-lg",
        availableDates = [], 
        colorUnavailableDates = false
    }) => {
    const parseLocalDate = (dateStr: string) => {
        const [y, m, d] = dateStr.split("-").map(Number);
        return new Date(y, m - 1, d);
    };

    const toLocalDateString = (date: Date) => {
        const y = date.getFullYear();
        const m = String(date.getMonth() + 1).padStart(2, "0");
        const d = String(date.getDate()).padStart(2, "0");
        return `${y}-${m}-${d}`;
    };
    const hasError = Boolean(errors[name]);
    const [availableStrings, setAvailableStrings] = useState<Set<string> | null>(null);
    useEffect(() => {
        if(availableDates && availableDates.length > 0){
            const availableStringsSet = new Set(
                availableDates?.map(d => toLocalDateString(d))
            );
            setAvailableStrings(availableStringsSet);
        }
    }, [availableDates]);
    return (
        <div className={wrapperClassName}>
            {label && <label htmlFor={name} className="form-label">
                {label}
                {required && <span className="text-danger">*</span>}
            </label>}
            <Controller
                control={control}
                name={name}
                rules={{
                    required: required ? "This field is required." : false
                }}
                render={({ field }) => (
                    <>
                        <DatePicker
                            id={name}
                            placeholderText="Select Date"
                            selected={field.value ? parseLocalDate(field.value) : null}
                            onChange={(date: Date | null) => {
                                field.onChange(date ? toLocalDateString(date) : null);
                            }}
                            className={className}
                            isClearable
                            // if no availableDates: allow all dates
                            includeDates={
                                availableDates.length > 0 && !colorUnavailableDates
                                    ? availableDates
                                    : undefined
                            }
                            dayClassName={(date) => {
                                if (!availableStrings || !colorUnavailableDates) return undefined;
                                const key = toLocalDateString(date);
                                const isAvailable = availableStrings.has(key);
                                return !isAvailable ? "unavailable-day" : undefined;
                            }}
                            dateFormat="yyyy-MM-dd"
                            popperPlacement="bottom-start"
                            showPopperArrow={false}
                            popperProps={{ middleware: [offset(10)] } as any}
                            customInput={
                                <CustomInput
                                    className={`${className}${hasError ? " is-invalid" : ""}`}
                                />
                            }
                        />
                        
                    </>
                )}
            />
        </div>
    );
};
